import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface PublicSubscriptionStatus {
  isActive: boolean;
  tier: 'basic' | 'premium' | 'enterprise' | null;
  hasContactsAccess: boolean;
  hasReservationsAccess: boolean;
  hasCateringAccess: boolean;
}

export const usePublicSubscriptionStatus = () => {
  const { data: settings, isLoading } = useQuery<PublicSubscriptionStatus>({
    queryKey: ['public-subscription-status'],
    queryFn: async () => {
      const { data: settingsData, error: settingsError } = await supabase
        .from('subscription_settings')
        .select('subscription_disabled, basic_price_id, premium_price_id, enterprise_price_id, manual_override_active, manual_override_tier')
        .limit(1)
        .single();
      
      if (settingsError) {
        console.error('Error fetching subscription settings:', settingsError);
        return { isActive: false, tier: null, hasContactsAccess: false, hasReservationsAccess: false, hasCateringAccess: false };
      }
      
      // Manual override: enable site-wide access without Stripe (takes priority over subscription_disabled)
      if (settingsData.manual_override_active) {
        const tier = (settingsData.manual_override_tier as 'basic' | 'premium' | 'enterprise' | null) ?? 'enterprise';
        return {
          isActive: true,
          tier,
          hasContactsAccess: tier === 'basic' || tier === 'premium' || tier === 'enterprise',
          hasReservationsAccess: tier === 'premium' || tier === 'enterprise',
          hasCateringAccess: tier === 'enterprise',
        };
      }

      // If subscription is explicitly disabled, no features are available
      if (settingsData.subscription_disabled) {
        return { isActive: false, tier: null, hasContactsAccess: false, hasReservationsAccess: false, hasCateringAccess: false };
      }

      // Try to get the owner's subscription tier from stripe_subscriptions
      const { data: subscriptions } = await supabase
        .from('stripe_subscriptions')
        .select('price_id, status')
        .in('status', ['active', 'trialing'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (!subscriptions || subscriptions.length === 0) {
        return { isActive: false, tier: null, hasContactsAccess: false, hasReservationsAccess: false, hasCateringAccess: false };
      }

      const priceId = subscriptions[0].price_id;
      
      // Determine tier and feature access
      let tier: 'basic' | 'premium' | 'enterprise' | null = null;
      let hasContactsAccess = false;
      let hasReservationsAccess = false;
      let hasCateringAccess = false;

      if (priceId === settingsData.basic_price_id) {
        tier = 'basic';
        hasContactsAccess = true;
      } else if (priceId === settingsData.premium_price_id) {
        tier = 'premium';
        hasContactsAccess = true;
        hasReservationsAccess = true;
      } else if (priceId === settingsData.enterprise_price_id) {
        tier = 'enterprise';
        hasContactsAccess = true;
        hasReservationsAccess = true;
        hasCateringAccess = true;
      }
      
      return { 
        isActive: true,
        tier,
        hasContactsAccess,
        hasReservationsAccess,
        hasCateringAccess
      };
    },
    refetchInterval: 30000,
    staleTime: 10000,
  });

  return {
    isActive: settings?.isActive ?? false,
    tier: settings?.tier ?? null,
    hasContactsAccess: settings?.hasContactsAccess ?? false,
    hasReservationsAccess: settings?.hasReservationsAccess ?? false,
    hasCateringAccess: settings?.hasCateringAccess ?? false,
    isLoading,
  };
};
